<template>
  <div id="Muck-results-grid-view">
    <div class="flex flex-wrap items-center">
      <div class="flex-grow">&nbsp;</div>
    </div>
    <ag-grid-vue
        ref="agGridTable"
        :components="components"
        :frameworkComponents="frameworkComponents"
        :gridOptions="gridOptions"
        :context="context"
        class="ag-theme-material w-100 my-4 ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="muckList"
        rowSelection="multiple"
        colResizeDefault="shift"
        :animateRows="true"
        :pagination="false"
        :suppressPaginationPanel="true"
        :enableRtl="$vs.rtl">
    </ag-grid-vue>
    <vs-popup class="holamundo" :title="$t('GradeControl.Timesheet.Versions')" :active.sync="versionPopupActive">
      <timesheet-version style="width:750px;"
        :timesheetId="timesheetStagingEntryId"
        @onClosed="versionClosed"
      />
    </vs-popup>
  </div>
</template>
<script>
import Vue from "vue";
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";

import CellMuckActions from './CellMuckActions.vue'
import CellRendererEquipment from './../../components/CellRendererEquipment.vue'
import CellEditorEquipmentId from './../../components/CellEditorEquipmentId.vue'
import ShiftEnum from "./../../../../shared/ShiftEnum.js";
import ElementEnum from '@/app/shared/ElementEnum';
import GradeCalculationTypeEnum  from "../../GradeCalculationTypeEnum.js";
import TimesheetVersion from './timesheetVersion.vue'
import MaterialTypeCellRenderer from './../../components/MaterialTypeCellRenderer.vue'
import MaterialTypeCellEditor from './../../components/MaterialTypeCellEditor.vue'


export default {
  name:"MuckListGrid",
  /* eslint-disable vue/no-unused-components */
  components: {
    AgGridVue,
    CellMuckActions,
    CellEditorEquipmentId,
    TimesheetVersion
  },
  props: {
    gradeCalculationType: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      defaultColDef: {
        sortable: true,
        resizable: true
      },
      gridApi: null,
      gridOptions: {
        getRowNodeId: (data) => {
          return data.muckId;
        }
      },
      context: { componentParent: this },
      columnDefs: [
        {
          headerName: 'Actions',
          field: 'sampleCount',
          width: 150,
          cellRendererFramework: Vue.extend(CellMuckActions),
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true
        },
        {
          headerName: this.$t('GradeControl.ExtractionDateShort'),
          field: "extractionDate",
          width: 150,
          valueFormatter: this.formatDateTime
        },
        {
          headerName: this.$t('GradeControl.Timesheet.BlastDateShort'),
          field: "blastDate",
          width: 150,
          valueFormatter: this.formatDateTime
        },
        {
          headerName: this.$t('GradeControl.Equipment.Name'),
          field: "equipmentId",
          width: 150,
          editable: false,
          cellEditorFramework: Vue.extend(CellEditorEquipmentId),
          cellRendererFramework: Vue.extend(CellRendererEquipment),
          valueGetter: (params) => {
            if (params.data.equipmentId !== null) {
              const equipment = params.context.componentParent.equipments.find(m => m.equipmentId === params.data.equipmentId)
              if (equipment) {
                return {
                  equipmentId: params.data.equipmentId,
                  code: equipment.code,
                  company: equipment.company
                };
              }
              else {
                return null;
              }
            }
            else {
              return null;
            }
          }
        },
        {
          headerName: this.$t('GradeControl.Shift'),
          field: "shift",
          width: 100,
          editable: false,
          valueGetter: (params) => {
            return this.$t("Shift." + params.data.shift);
          },
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: [this.$t("Shift." + ShiftEnum.Day), this.$t("Shift." + ShiftEnum.Night)]
          }
        },
        {
          headerName: this.$t('GradeControl.BucketCount'),
          field: "bucketCount",
          width: 110,
          editable: false
        },
        {
          headerName: this.$t('GradeControl.MuckTypeShort'),
          field: "muckTypeId",
          width: 130,
          editable: false,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: this.getMuckTypeCellEditor.bind(this),
          valueGetter: (params) => {
            if (params.data.muckTypeId !== null) {
              const muckType = params.context.componentParent.muckTypes.find(m => m.muckTypeId === params.data.muckTypeId)
              if (muckType) {
                return muckType.name;
              }
              else {
                return null;
              }
            }
            else {
              return null;
            }
          },
          valueSetter: (params) => {
            const muckType = params.context.componentParent.muckTypes.find(m => m.name === params.newValue)
            if (typeof muckType !== 'undefined') {
              params.data.muckTypeId = muckType.muckTypeId;
              return true;
            }
            else {
              return false;
            }
          }
        },
        {
          headerName: this.$t('GradeControl.Timesheet.MaterialType'),
          field: "materialType",
          filter: true,
          editable: false,
          filterParams: {
            valueGetter: params => {
              return this.$t("Material." + params.data.materialType);
            }
          },
          width: 180,
          comparator:  (valueA, valueB) => {
            if (valueA.materialType === valueB.materialType) {
              return 0;
            }
            else {
              return (this.$t("Material." + valueA.materialType) > this.$t("Material." + valueB.materialType)) ? 1 : -1;
            }
          },
          cellRendererFramework: Vue.extend(MaterialTypeCellRenderer),
          cellEditorFramework: Vue.extend(MaterialTypeCellEditor),
          valueGetter: (params) => {
            const materialType = params.data.materialType;
            const materialSubTypeId = params.data.materialSubTypeId;
            return {
              materialType,
              materialSubTypeId
            };
          },
          valueSetter: (params) => {
            if (params.data.materialType !== null && params.data.materialType !== '') {
              params.data.materialType = params.newValue.materialType;
              params.data.materialSubTypeId = params.newValue.materialSubTypeId;
              return true;
            } else {
              return false;
            }
          }
        },
        {
          headerName: this.$systemUnits.getTonnageLabel(),
          field: "tonnage",
          width: 180,
          editable: false,
          valueFormatter: this.formatTonnage,
          valueGetter: (params) => this.$systemUnits.convertTonnage(params.data.tonnage)
        },
        {
          headerName: this.$t('GradeControl.Timesheet.RoundNumber'),
          field: "roundNumber",
          width: 210,
          editable: false
        },
        {
          headerName: this.$t('GradeControl.Miner'),
          field: "minerName",
          width: 125,
          editable: false
        },
        {
          headerName: this.$systemUnits.getGradeLabel('Gold'),
          field: "goldGrade",
          width: 125,
          valueFormatter: this.formatGrade,
          valueGetter: (params) => {
            const results = this.getGradeResults(params.data, this.$props.gradeCalculationType);
            const result = results.find(g => g.element === ElementEnum.Gold);
            if (result)
              return this.$systemUnits.convertGrade(result.grade);

            return "";

          }
        },
        {
          headerName: this.$systemUnits.getGradeLabel('Silver'),
          field: "silverGrade",
          width: 135,
          valueFormatter: this.formatGrade,
          valueGetter: (params) => {
            const results = this.getGradeResults(params.data, this.$props.gradeCalculationType);
            const result = results.find(g => g.element === ElementEnum.Silver);
            if (result)
              return this.$systemUnits.convertGrade(result.grade);

            return "";

          }
        },
        {
          headerName: this.$t('Copper') + ' (%)',
          field: "copperGrade",
          width: 135,
          valueFormatter: this.formatGrade,
          valueGetter: (params) => {
            const results = this.getGradeResults(params.data, this.$props.gradeCalculationType);
            const result = results.find(g => g.element === ElementEnum.Copper);
            if (result)
              return result.grade;

            return "";

          }
        },
        {
          headerName: this.$t('Zinc') + ' (%)',
          field: "zincGrade",
          width: 125,
          valueFormatter: this.formatGrade,
          valueGetter: (params) => {
            const results = this.getGradeResults(params.data, this.$props.gradeCalculationType);
            const result = results.find(g => g.element === ElementEnum.Zinc);
            if (result)
              return result.grade;

            return "";

          }
        },
        {
          headerName: this.$t('Lead') + ' (%)',
          field: "leadGrade",
          width: 125,
          valueFormatter: this.formatGrade,
          valueGetter: (params) => {
            const results = this.getGradeResults(params.data, this.$props.gradeCalculationType);
            const result = results.find(g => g.element === ElementEnum.Lead);
            if (result)
              return result.grade;

            return "";

          }
        },
        {
          headerName: this.$t('Carbon') + ' (%)',
          field: "carbonGrade",
          width: 135,
          valueFormatter: this.formatGrade,
          valueGetter: (params) => {
            const results = this.getGradeResults(params.data, this.$props.gradeCalculationType);
            const result = results.find(g => g.element === ElementEnum.Carbon);
            if (result)
              return result.grade;

            return "";

          }
        },
        {
          headerName: this.$t('Sulfur') + ' (%)',
          field: "sulfurGrade",
          width: 125,
          valueFormatter: this.formatGrade,
          valueGetter: (params) => {
            const results = this.getGradeResults(params.data, this.$props.gradeCalculationType);
            const result = results.find(g => g.element === ElementEnum.Sulfur);
            if (result)
              return result.grade;

            return "";

          }
        },
        {
          headerName: this.$t('GradeControl.Muck.ReceivedSampleRate'),
          field: "receivedSampleRate",
          valueFormatter: this.formatPercentage,
          valueGetter: (params) => {
            if (params.data.totalSamples === 0)
              return "N/A";

            return params.data.receivedSampleRate;

          },
          width: 220
        },
        {
          headerName: this.$t('GradeControl.Muck.ApprovedBy'),
          field: "approvedBy",
          width: 150
        },
        {
          headerName: this.$t('GradeControl.Muck.ApprovalDateTime'),
          field: "approvalDateTime",
          width: 160,
          valueFormatter: this.formatDateTime
        }
      ],
      components: {
        CellMuckActions,
        CellRendererEquipment,
        MaterialTypeCellRenderer
      },
      frameworkComponents: {
        CellEditorEquipmentId,
        MaterialTypeCellEditor
      },
      versionPopupActive: false,
      timesheetStagingEntryId: null
    }
  },
  computed: {
    equipments() {
      return this.$store.state.gradeControl.equipments;
    },
    employees() {
      return this.$store.state.gradeControl.employees;
    },
    muckTypes() {
      return this.$store.state.gradeControl.muckTypes;
    },
    muckTypeNames() {
      return this.$store.state.gradeControl.muckTypes.map(l => l.name);
    },
    muckList() {
      return this.$store.state.gradeControl.mucks;
    }
  },
  methods: {
    getGradeResults(muck, gradeCalculation) {
      switch (gradeCalculation) {
      case GradeCalculationTypeEnum.SampleOnly:
        return muck.sampleOnlyResults;
      case GradeCalculationTypeEnum.SampleOrForecast:
        return muck.sampleOrForecastResults;
      case GradeCalculationTypeEnum.SampleOnlyCapped:
        return muck.sampleOnlyCappedResults;
      case GradeCalculationTypeEnum.SampleOrForecastCapped:
        return muck.sampleOrForecastCappedResults;
      }
    },
    getMuckTypeCellEditor() {
      return {
        values : this.muckTypeNames
      }
    },
    formatDateTime(params) {
      return this.$options.filters.toDate(params.value);
    },
    formatTonnage(params) {
      return this.$options.filters.toInteger(params.value);
    },
    getMuckEntries(locationForecastId) {
      if (locationForecastId) {
        return this.$store.dispatch("gradeControl/fetchForecastMucksIncludeAllMaterialType", locationForecastId);
      }
    },
    updateGrid(muck) {
      const rowNode = this.gridApi.getRowNode(muck.muckId);
      if (rowNode) {
        rowNode.setData(muck);
      }
    },
    selectMuck(data) {
      this.$emit("onSelectMuck", data);
    },
    insertMuckInGrid(muck) {
      this.gridApi.applyTransaction({ add: [muck] });
    },
    formatGrade(params) {
      return this.$options.filters.toFriendlyGrade(params.value);
    },
    formatPercentage(params) {
      return this.$options.filters.toPercentage(params.value);
    },
    openVersionPopup(muck) {
      this.timesheetStagingEntryId = muck.timesheetStagingEntryId;
      this.showVersion(true);
    },
    showVersion(val = false) {
      this.versionPopupActive = val;
    },
    versionClosed() {
      this.versionPopupActive = false;
    }
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
  }
};
</script>
<style lang="scss">
  #Muck-results-grid-view {
    .ag-header-group-text {
      text-align: center;
      border: 1px;
    }
    .ag-header-group-cell-label {
      align-content: center;
    }
  }
  .con-vs-popup .vs-popup {
    width: auto !important;
  }
</style>
